import type { InboxResponse } from '@pulse/contracts';
import * as React from 'react';
import { Link } from 'react-router-dom';

import { AttentionItem } from '@/components/pulse/attention-item';
import { AsOfPill } from '@/components/pulse/day';
import { ArrowIcon, ExternalIcon } from '@/components/pulse/icons';
import { PageHeader } from '@/components/pulse/page-header';
import { SourceLink } from '@/components/pulse/source-link';
import { useInbox } from '@/domains/inbox/api';
import { useShell } from '@/domains/shell/api';

/** Inbox (INB-1 to INB-9), built from the prototype's `viewInbox()`. A current-state page: as of now. */
export function InboxPage(): React.ReactElement {
  const page = useInbox();
  const shell = useShell();
  const name = page.data?.memberName ?? null;
  return (
    <>
      <PageHeader
        eyebrow={`Inbox · ${page.data?.dateLabel ?? ''}`}
        title="Waiting on you"
        sub={`Everything that needs ${name ?? 'you'} to act, from GitHub, Teamwork and Talos. Oldest problems last.`}
        right={<AsOfPill label={shell.data?.freshness.asOfLabel ?? null} />}
      />
      {page.data !== undefined ? <InboxView data={page.data} /> : null}
    </>
  );
}

export function InboxView({ data }: { readonly data: InboxResponse }): React.ReactElement {
  return (
    <div className="two" style={{ gridTemplateColumns: 'minmax(0,1fr) 340px' }}>
      <div className="stack" style={{ gap: 16 }}>
        {data.groups.length === 0 ? (
          <section className="card pad">
            <span style={{ color: 'var(--ink-2)' }}>
              {!data.recorded ? 'Not collected yet.' : data.memberName === null ? "You aren't on the roster, so nothing here waits on you." : 'Nothing is waiting on you.'}
            </span>
          </section>
        ) : null}
        {data.groups.map((group) => (
          <section key={group.group} className="card pad stack" style={{ gap: 12 }}>
            <div className="row" style={{ justifyContent: 'space-between', gap: 12 }}>
              <h2>{group.group}</h2>
              <span className={`chip ${group.tone}`}>{group.rows.length}</span>
            </div>
            <span style={{ fontSize: 13, color: 'var(--muted)' }}>{group.why}</span>
            <div className="grid-items">
              {group.rows.map((row) => (
                <AttentionItem key={`${row.ref}${row.url}`} row={row} />
              ))}
            </div>
          </section>
        ))}
      </div>
      <aside className="stack" style={{ gap: 16 }}>
        {data.acceptance !== null ? (
          <Link className="card pad stack" to={data.acceptance.href} style={{ gap: 8, textDecoration: 'none', color: 'var(--ink)' }}>
            <h2>Stories to accept</h2>
            <span className="kpi-value num" style={data.acceptance.waiting > 0 ? { color: 'var(--warn-ink)' } : undefined}>
              {data.acceptance.waiting}
            </span>
            <span style={{ color: 'var(--ink-2)' }}>{data.acceptance.line}</span>
            <span className="linkish" style={{ fontSize: 13 }}>
              Open the acceptance queue <ArrowIcon />
            </span>
          </Link>
        ) : null}
        {data.stale !== null ? (
          <section className="card pad stack" style={{ gap: 8 }}>
            <h2>Stale review requests</h2>
            <span style={{ color: 'var(--ink-2)' }}>{data.stale.line}</span>
            {data.stale.count > 0 ? (
              <SourceLink className="linkish" href={data.stale.url} style={{ fontSize: 13 }}>
                Review or clear them on GitHub <ExternalIcon />
              </SourceLink>
            ) : null}
          </section>
        ) : null}
        {data.memberName !== null ? (
          <section className="card pad stack" style={{ gap: 8 }}>
            <h2>Mentions</h2>
            {data.mentions.length === 0 ? <span style={{ color: 'var(--ink-2)' }}>Nothing open mentioned you this week.</span> : null}
            {data.mentions.map((row) => (
              <AttentionItem key={row.url} row={row} />
            ))}
          </section>
        ) : null}
      </aside>
    </div>
  );
}
